import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useShallow } from 'zustand/react/shallow'

import Header from '../../components/Header'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@harvey-frontend/components/ui/tabs'

import useAppStore from '../../stores/store'
import { dedupeReferencePages } from '../../utils/playbook-utils'
import { RuleDetails } from './RuleDetails'
import SearchFilter from './SearchFilter'
import { StatusSection } from './StatusSection'

const PlaybookReviewScreen = () => {
  const location = useLocation()
  const [search, setSearch] = useState('')
  const [expandedSection, setExpandedSection] = useState<string | null>(null)
  const { reviewResult, playbook } = useAppStore(
    useShallow((state) => ({
      reviewResult: state.reviewResult,
      playbook: state.playbook,
    }))
  )

  const resolvedItems =
    reviewResult?.review.filter((item) => item.resolved) ?? []

  const sections = useMemo(
    () => [
      {
        id: 'needs_review',
        title: 'Needs review',
        color: 'gold',
        items:
          reviewResult?.review.filter(
            (item) => item.status === 'needs_review'
          ) ?? [],
      },
      {
        id: 'unacceptable',
        title: 'Not acceptable',
        color: 'red',
        items:
          reviewResult?.review.filter(
            (item) => item.status === 'unacceptable'
          ) ?? [],
      },
      {
        id: 'acceptable',
        title: 'Acceptable',
        color: 'green',
        items:
          reviewResult?.review.filter((item) => item.status === 'acceptable') ??
          [],
      },
    ],
    [reviewResult]
  )

  useEffect(() => {
    // If we have a section to expand from navigation state, use that
    if (location.state?.expandedSection) {
      setExpandedSection(location.state.expandedSection)
    } else {
      // Otherwise, set the first nonempty section to be expanded
      const firstNonemptySection = sections.find(
        (section) => section.items.filter((item) => !item.resolved).length > 0
      )
      if (firstNonemptySection) {
        setExpandedSection(firstNonemptySection.id)
      } else {
        setExpandedSection(null)
      }
    }
  }, [sections, location.state?.expandedSection])

  const getStartIndex = (sectionIndex: number) =>
    sections
      .slice(0, sectionIndex)
      .reduce((sum, section) => sum + section.items.length, 0)

  const handleToggle = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section)
  }

  const sectionContent = sections.map((section, index) => (
    <StatusSection
      key={section.id}
      items={section.items}
      totalItems={section.items.filter((item) => !item.resolved).length}
      startIndex={getStartIndex(index)}
      color={section.color}
      title={section.title}
      search={search}
      isCollapsed={expandedSection !== section.id && !search}
      onToggle={() => handleToggle(section.id)}
    />
  ))

  if (!playbook) {
    return null
  }

  return (
    <div>
      <Header title="Summary" to="/playbook-selection" />
      <div className="m-4 flex flex-col gap-2">
        <SearchFilter value={search} onChange={setSearch} />

        {resolvedItems.length > 0 ? (
          <Tabs defaultValue="review" className="w-full">
            <TabsList variant="minimal" className="mb-2 w-full">
              <TabsTrigger
                variant="minimal"
                value="review"
                // Horrifying but this is what Assistant uses on the main app to get the "selected" effect.
                className="text-sm data-[state=active]:shadow-[inset_0_-1px_0_0_hsl(var(--text-primary)),0_1px_0_0_hsl(var(--text-primary))]"
              >
                Review
              </TabsTrigger>
              <TabsTrigger
                variant="minimal"
                value="resolved"
                className="text-sm data-[state=active]:shadow-[inset_0_-1px_0_0_hsl(var(--text-primary)),0_1px_0_0_hsl(var(--text-primary))]"
              >
                Resolved
              </TabsTrigger>
            </TabsList>
            <TabsContent value="review" className="flex w-full flex-col gap-2">
              {sectionContent}
            </TabsContent>
            <TabsContent
              value="resolved"
              className="flex w-full flex-col gap-2"
            >
              {resolvedItems.map((item) => (
                <RuleDetails
                  key={item.ruleName}
                  rule={
                    playbook.rules.find((rule) => rule.name === item.ruleName)!
                  }
                  headerContent={
                    <div className="flex flex-1 items-center justify-between gap-2">
                      <div className="flex flex-col items-start">
                        <span className="text-xs text-primary">
                          {item.ruleName}
                        </span>
                        <span className="text-xs text-secondary">
                          {dedupeReferencePages(item.references)}
                        </span>
                      </div>
                    </div>
                  }
                />
              ))}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="flex flex-col gap-2">{sectionContent}</div>
        )}
      </div>
    </div>
  )
}

export default PlaybookReviewScreen
